import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Navbar from "../../layout/Navbar";
import Footer from "../../layout/Footer";

const ApplyAsATutor = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        full_name: "",
        email: "",
        phone: "",
        qualification: "",
        expertise: [],
        subjects: "",
        region: "",
        experience: "",
        education_level: [],
        availability: "",
        hourly_rate: "",
        teaching_philosophy: "",
        video_introduction: "",
        terms_accepted: false
    });
    
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [certificationFiles, setCertificationFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applicationId, setApplicationId] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [activeStep, setActiveStep] = useState(1);

    const fileInputRef = useRef(null);

    const expertiseOptions = [
        "BECE", "SHS/WASSCE", "CTVET", "University", 
        "Project Supervision", "Dissertation Guidance", 
        "Online Tutoring", "Exam Preparation"
    ];
    
    const educationLevelOptions = [
        "Bachelor's Degree", "Master's Degree", "PhD", 
        "Professional Certification", "Teaching Certificate", "Other"
    ];

    const regions = [
        "Greater Accra Region", "Ashanti Region", "Ahafo Region", 
        "Nothern Region", "Eastern Region", "Central Region", 
        "Western Region", "Savannah Region", "Upper East Region",
        "Upper West Region", "Volta Region", "Oti Region",
        "Bono East Region", "Bono Region", "North East Region",
        "Western North Region"
    ];
    
    const subjectCategories = [
        "Mathematics", "Physics", "Biology", "Chemistry", "Cybersecurity", "Creative Art", "Science", "English Language & Literature", 
        "Social Studies", "ICT/Computer Studies", "Business Studies", 
        "Accounting", "Economics", "Government", "Geography", 
        "History", "French", "Technical Drawing", "Home Economics", 
        "Religious & Moral Education"
    ];

    const steps = [
        { id: 1, name: "Personal Info", icon: "bi-person" },
        { id: 2, name: "Expertise", icon: "bi-award" },
        { id: 3, name: "Documents", icon: "bi-folder" }
    ];

    // Helper function to safely get form values
    const getFormValue = (fieldName) => {
        return formData[fieldName] || "";
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            if (name === 'expertise' || name === 'education_level') {
                const updatedArray = formData[name].includes(value)
                    ? formData[name].filter(item => item !== value)
                    : [...formData[name], value];
                setFormData(prev => ({ ...prev, [name]: updatedArray }));
            } else {
                setFormData(prev => ({ ...prev, [name]: checked }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value || "" }));
        }
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error("Please select a valid image file (JPEG, PNG, GIF, WebP)");
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error("Profile picture size should be less than 5MB");
            return;
        }

        setProfilePicture(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfilePreview(reader.result);
        };
        reader.readAsDataURL(file);

        toast.info("Profile picture selected");
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (name === 'resume') {
            const file = files[0];
            if (file) {
                // Validate file type
                const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                if (!validTypes.includes(file.type)) {
                    toast.error("Please select a valid file (PDF, DOC, DOCX)");
                    e.target.value = '';
                    return;
                }
                setResumeFile(file);
                toast.info("Resume file selected");
            }
        } else if (name === 'certifications') {
            const filesArray = Array.from(files);
            
            // Validate each file
            const validFiles = [];
            filesArray.forEach(file => {
                const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
                if (validTypes.includes(file.type)) {
                    validFiles.push(file);
                } else {
                    toast.error(`Skipped ${file.name}: Invalid file type. Only PDF, JPG, PNG allowed`);
                }
            });
            
            setCertificationFiles(validFiles);
            if (validFiles.length > 0) {
                toast.info(`${validFiles.length} certification file(s) selected`);
            }
        }
    };

    const removeProfilePicture = () => {
        setProfilePicture(null);
        setProfilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toast.info("Profile picture removed");
    };

    const validateForm = () => {
        // Step 1 validation
        if (activeStep === 1) {
            if (!formData.full_name.trim()) {
                toast.error("Full name is required");
                return false;
            }
            if (!profilePicture) {
                toast.error("Profile Picture Required");
                return false;
            }
            if (!formData.email.trim()) {
                toast.error("Email is required");
                return false;
            }
            if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
                toast.error("Please enter a valid email address");
                return false;
            }
            if (!formData.phone.trim()) {
                toast.error("Phone number is required");
                return false;
            }
            if (!/^(\+233|0)[0-9]{9}$/.test(formData.phone.replace(/\s/g, ''))) {
                toast.error("Please enter a valid Ghanaian phone number (format: +233XXXXXXXXX or 0XXXXXXXXX)");
                return false;
            }
        }

        // Step 2 validation
        if (activeStep === 2) {
            if (formData.expertise.length === 0) {
                toast.error("Please select at least one area of expertise");
                return false;
            }
            if (!formData.subjects) {
                toast.error("Please select subjects you can teach");
                return false;
            }
        }

        // Step 3 validation
        if (activeStep === 3) {
            if (!resumeFile) {
                toast.error("Resume file is required");
                return false;
            }
            if (!formData.terms_accepted) {
                toast.error("You must accept the terms and conditions");
                return false;
            }
        }

        return true;
    };

    const nextStep = () => {
        if (validateForm()) {
            setActiveStep(prev => Math.min(prev + 1, 3));
        }
    };

    const prevStep = () => {
        setActiveStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        toast.info("Submitting application...");

        try {
            const submitData = new FormData();
            
            // Add profile picture if exists
            if (profilePicture) {
                submitData.append('profile_picture', profilePicture);
            }
            
            Object.keys(formData).forEach(key => {
                if (key === 'expertise' || key === 'education_level') {
                    formData[key].forEach(item => submitData.append(`${key}[]`, item));
                } else if (key !== 'terms_accepted') {
                    submitData.append(key, formData[key]);
                }
            });

            if (resumeFile) submitData.append('resume', resumeFile);
            
            if (certificationFiles.length > 0) {
                certificationFiles.forEach(file => {
                    submitData.append('certifications[]', file);
                });
            }

            const API_URL = "https://pastq.eddcodesgh.com/qApi/client/tutors/_apply.php";
            const API_KEY = "f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506";

            const response = await axios.post(API_URL, submitData, {
                headers: {
                    'Authorization': `Bearer ${API_KEY}`,
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            if (response.data.success) {
                const applicationId = response.data.data?.application_id;
                
                if (!applicationId) {
                    console.error('No application_id in response:', response.data);
                    toast.error('Failed to get application ID. Please contact support.');
                    return;
                }
                
                // Store application data
                setApplicationId(applicationId);
            
                // Store in localStorage
                try {
                    // Clear any existing data first
                    localStorage.removeItem('tutorAppId');
                    localStorage.removeItem('fullName');
                    localStorage.removeItem('email');
                    localStorage.removeItem('phone');
                    
                    // Set new data
                    localStorage.setItem('tutorAppId', applicationId);
                    localStorage.setItem('fullName', formData.full_name);
                    localStorage.setItem('email', formData.email);
                    localStorage.setItem('phone', formData.phone);
                 
                    // Also store in sessionStorage as backup
                    sessionStorage.setItem('tutorAppId', applicationId);
                    sessionStorage.setItem('fullName', formData.full_name);
                    sessionStorage.setItem('email', formData.email);
                    sessionStorage.setItem('phone', formData.phone);
                    
                } catch (storageError) {
                    console.error('LocalStorage error:', storageError);
                    toast.warning("Application saved but unable to store data locally.");
                }
                
                toast.success(
                    <div className="toast-success">
                        <h6 className="mb-2">Application Submitted Successfully!</h6>
                        <p className="mb-1"><strong>Application ID:</strong> {applicationId}</p>
                        <p className="mb-0">We'll contact you within 3-5 business days.</p>
                    </div>,
                    { autoClose: 8000 }
                );
                
                // Wait a moment to ensure storage is complete
                await new Promise(resolve => setTimeout(resolve, 100));
                
                console.log('Before navigation - localStorage:', localStorage.getItem('tutorAppId'));
                
                // Navigate to payment page
                navigate("/payment", { 
                    state: { 
                        applicationId: applicationId,
                        fullName: formData.full_name,
                        email: formData.email,
                        phone: formData.phone
                    } 
                });
                
            } else {
                toast.error(response.data.message || "Application failed. Please try again.");
            }
        } catch (err) {
            console.error('Submit Error:', err);
            let errorMessage = "Network error. Please check your connection and try again.";
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
            
            localStorage.removeItem('tutorAppId');
            sessionStorage.removeItem('tutorAppId');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (activeStep) {
            case 1:
                return (
                    <div className="step-content">
                        <h4 className="fw-bold mb-4">
                            <i className="bi bi-person-badge me-2 text-primary"></i>
                            Personal Information
                        </h4>
                        <div className="row g-3">
                            {/* Profile Picture Upload */}
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Profile Picture (Needed) <span className="text-danger">*</span>
                                    </label>
                                    <div className="profile-upload-container">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="profile-preview-container">
                                                {profilePreview ? (
                                                    <div className="position-relative">
                                                        <img 
                                                            src={profilePreview} 
                                                            alt="Profile Preview" 
                                                            className="profile-preview rounded-circle"
                                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                                        />
                                                        <button 
                                                            type="button"
                                                            className="btn btn-sm btn-danger btn-icon position-absolute top-0 end-0 rounded-circle"
                                                            onClick={removeProfilePicture}
                                                            style={{ width: '30px', height: '30px', padding: 0 }}
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="profile-placeholder rounded-circle d-flex align-items-center justify-content-center bg-light"
                                                         style={{ width: '100px', height: '100px' }}>
                                                        <i className="bi bi-person fs-1 text-muted"></i>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png,.gif,.webp"
                                                    onChange={handleProfilePictureChange}
                                                    className="form-control"
                                                    style={{ display: 'none' }}
                                                    id="profilePicture"
                                                    required
                                                />
                                                <label htmlFor="profilePicture" className="btn btn-outline-primary mb-2">
                                                    <i className="bi bi-upload me-2"></i>
                                                    {profilePicture ? 'Change Picture' : 'Upload Picture'}
                                                </label>
                                                <div>
                                                    <small className="text-muted">
                                                        Recommended: Square image, max 5MB. JPG, PNG, GIF, or WebP.
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Full Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        className="form-control"
                                        value={getFormValue('full_name')}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter your full name"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Email Address <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-control"
                                        value={getFormValue('email')}
                                        onChange={handleChange}
                                        required
                                        placeholder="you@example.com"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Phone Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        className="form-control"
                                        value={getFormValue('phone')}
                                        onChange={handleChange}
                                        required
                                        placeholder="+233 XX XXX XXXX"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Region <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="region"
                                        className="form-select"
                                        value={getFormValue('region')}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Region</option>
                                        {regions.map((region, index) => (
                                            <option key={index} value={region}>{region}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Highest Qualification
                                    </label>
                                    <input
                                        type="text"
                                        name="qualification"
                                        className="form-control"
                                        value={getFormValue('qualification')}
                                        onChange={handleChange}
                                        placeholder="e.g., M.Ed in Mathematics"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="step-content">
                        <h4 className="fw-bold mb-4">
                            <i className="bi bi-award me-2 text-primary"></i>
                            Teaching Expertise
                        </h4>
                        <div className="row g-3">
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="form-label fw-semibold mb-2">
                                        Areas of Expertise <span className="text-danger">*</span>
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {expertiseOptions.map((expertise, index) => (
                                            <div key={index} className="form-check form-check-inline">
                                                <input
                                                    type="checkbox"
                                                    id={`expertise-${index}`}
                                                    name="expertise"
                                                    value={expertise}
                                                    checked={formData.expertise.includes(expertise)}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                />
                                                <label 
                                                    htmlFor={`expertise-${index}`}
                                                    className="form-check-label"
                                                >
                                                    {expertise}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Subjects <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        name="subjects"
                                        className="form-select"
                                        value={getFormValue('subjects')}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select subject</option>
                                        {subjectCategories.map((subject, index) => (
                                            <option key={index} value={subject}>{subject}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Experience
                                    </label>
                                    <select
                                        name="experience"
                                        className="form-select"
                                        value={getFormValue('experience')}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select experience</option>
                                        <option value="0-1">Less than 1 year</option>
                                        <option value="1-3">1-3 years</option>
                                        <option value="3-5">3-5 years</option>
                                        <option value="5-10">5-10 years</option>
                                        <option value="10+">10+ years</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Education Level
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {educationLevelOptions.slice(0, 3).map((level, index) => (
                                            <div key={index} className="form-check form-check-inline">
                                                <input
                                                    type="checkbox"
                                                    id={`education-${index}`}
                                                    name="education_level"
                                                    value={level}
                                                    checked={formData.education_level.includes(level)}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                />
                                                <label 
                                                    htmlFor={`education-${index}`}
                                                    className="form-check-label"
                                                >
                                                    {level}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Hourly Rate (GHS)
                                    </label>
                                    <input
                                        type="number"
                                        name="hourly_rate"
                                        className="form-control"
                                        value={getFormValue('hourly_rate')}
                                        onChange={handleChange}
                                        placeholder="50"
                                        min="20"
                                        max="500"
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Availability
                                    </label>
                                    <select
                                        name="availability"
                                        className="form-select"
                                        value={getFormValue('availability')}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select availability</option>
                                        <option value="part-time">Part-time</option>
                                        <option value="full-time">Full-time</option>
                                        <option value="weekends">Weekends</option>
                                        <option value="flexible">Flexible</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Video Introduction (Optional)
                                    </label>
                                    <input
                                        type="url"
                                        name="video_introduction"
                                        className="form-control"
                                        value={getFormValue('video_introduction')}
                                        onChange={handleChange}
                                        placeholder="YouTube/Vimeo link"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="step-content">
                        <h4 className="fw-bold mb-4">
                            <i className="bi bi-folder me-2 text-primary"></i>
                            Documents & Final Step
                        </h4>
                        <div className="row g-3">
                            {/* Profile Picture Summary */}
                            {profilePicture && (
                                <div className="col-12">
                                    <div className="alert alert-info">
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            <div>
                                                <strong>Profile Picture Added</strong>
                                                <p className="mb-0 small">Your profile picture will be displayed on your tutor profile.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Resume/CV <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="file"
                                        name="resume"
                                        className="form-control"
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx"
                                        required
                                    />
                                    <small className="text-muted">Max 5MB: PDF, DOC, DOCX</small>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Certifications (Optional)
                                    </label>
                                    <input
                                        type="file"
                                        name="certifications"
                                        className="form-control"
                                        onChange={handleFileChange}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        multiple
                                    />
                                    <small className="text-muted">PDF, JPG, PNG formats</small>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-group">
                                    <label className="form-label fw-semibold">
                                        Teaching Philosophy (Optional)
                                    </label>
                                    <textarea
                                        name="teaching_philosophy"
                                        className="form-control"
                                        value={getFormValue('teaching_philosophy')}
                                        onChange={handleChange}
                                        rows="3"
                                        placeholder="Briefly describe your teaching approach..."
                                    ></textarea>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        name="terms_accepted"
                                        checked={formData.terms_accepted}
                                        onChange={handleChange}
                                        className="form-check-input"
                                        required
                                    />
                                    <label htmlFor="terms" className="form-check-label">
                                        I agree to the{' '}
                                        <a href="/terms" className="text-decoration-none">
                                            Terms and Conditions
                                        </a>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Navbar />
            <div className="container py-5">
                <div className="row justify-content-center mt-5">
                    <div className="col-lg-8 mt-3 py-2">
                        {/* Header */}
                        <div className="text-center mb-4">
                            <h1 className="h2 fw-bold mb-2">Apply as a Tutor</h1>
                            <p className="text-muted mb-3">
                                Join our network of expert educators
                            </p>
                            <div className="d-flex flex-wrap justify-content-center gap-2">
                                <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill">
                                    <i className="bi bi-coin me-1"></i>
                                    Competitive Rates
                                </span>
                                <span className="badge bg-success bg-opacity-10 text-success px-3 py-1 rounded-pill">
                                    <i className="bi bi-people me-1"></i>
                                    500+ Tutors
                                </span>
                                <span className="badge bg-warning bg-opacity-10 text-warning px-3 py-1 rounded-pill">
                                    <i className="bi bi-clock-history me-1"></i>
                                    Flexible Hours
                                </span>
                            </div>
                        </div>

                        {/* Stepper */}
                        <div className="stepper mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                {steps.map((step, index) => (
                                    <React.Fragment key={step.id}>
                                        <div className={`step-item ${activeStep === step.id ? 'active' : ''} ${activeStep > step.id ? 'completed' : ''}`}>
                                            <div className="step-icon">
                                                <i className={`bi ${step.icon}`}></i>
                                            </div>
                                            <div className="step-label d-none d-sm-block">{step.name}</div>
                                        </div>
                                        {index < steps.length - 1 && (
                                            <div className="step-line"></div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {loading && (
                            <div className="progress mb-3" style={{ height: '4px' }}>
                                <div 
                                    className="progress-bar progress-bar-striped progress-bar-animated" 
                                    role="progressbar" 
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        )}

                        {/* Application Form */}
                        <div className="application-form bg-white rounded-3 shadow-sm border p-4">
                            <form onSubmit={activeStep === 3 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }}>
                                {renderStep()}

                                {/* Navigation Buttons */}
                                <div className="d-flex justify-content-between mt-4">
                                    {activeStep > 1 ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={prevStep}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-arrow-left me-1"></i>
                                            Back
                                        </button>
                                    ) : (
                                        <div></div>
                                    )}
                                    
                                    {activeStep < 3 ? (
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            Next <i className="bi bi-arrow-right ms-1"></i>
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-send me-2"></i>
                                                    Submit Application
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        {/* Benefits Section */}
                        <div className="benefits-section mt-5">
                            <h5 className="fw-bold mb-3 text-center">Why Tutor With Us?</h5>
                            <div className="row g-3">
                                {[
                                    { icon: "bi-cash-coin", title: "Competitive Earnings", desc: "Set your own rates" },
                                    { icon: "bi-calendar-check", title: "Flexible Schedule", desc: "Teach on your terms" },
                                    { icon: "bi-people", title: "Large Student Base", desc: "Access thousands" },
                                    { icon: "bi-shield-check", title: "Secure Payments", desc: "Timely payments" }
                                ].map((benefit, index) => (
                                    <div key={index} className="col-6 col-lg-3">
                                        <div className="benefit-card text-center p-3 bg-white rounded-3 border h-100">
                                            <div className="benefit-icon mb-2">
                                                <i className={`bi ${benefit.icon} fs-4 text-primary`}></i>
                                            </div>
                                            <h6 className="fw-bold mb-1">{benefit.title}</h6>
                                            <p className="text-muted small mb-0">{benefit.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Success Message */}
                        {applicationId && (
                            <div className="alert alert-success mt-4">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-check-circle-fill fs-4 me-3"></i>
                                    <div>
                                        <strong>Application Submitted!</strong>
                                        <p className="mb-0">ID: {applicationId} - We'll contact you soon.</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .stepper {
                    position: relative;
                }
                
                .step-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 2;
                }
                
                .step-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #e9ecef;
                    color: #6c757d;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                }
                
                .step-item.active .step-icon {
                    background-color: #0d6efd;
                    color: white;
                    transform: scale(1.1);
                }
                
                .step-item.completed .step-icon {
                    background-color: #198754;
                    color: white;
                }
                
                .step-label {
                    font-size: 0.8rem;
                    margin-top: 0.5rem;
                    font-weight: 500;
                    color: #6c757d;
                }
                
                .step-item.active .step-label {
                    color: #0d6efd;
                    font-weight: 600;
                }
                
                .step-line {
                    flex-grow: 1;
                    height: 2px;
                    background-color: #e9ecef;
                    margin: 0 10px;
                    position: relative;
                    top: 20px;
                }
                
                .step-content {
                    animation: fadeIn 0.3s ease;
                }
                
                .profile-upload-container {
                    border: 2px dashed #dee2e6;
                    border-radius: 10px;
                    padding: 20px;
                    background-color: #f8f9fa;
                    transition: all 0.3s ease;
                }
                
                .profile-upload-container:hover {
                    border-color: #0d6efd;
                    background-color: #f0f8ff;
                }
                
                .profile-preview {
                    border: 3px solid white;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                }
                
                .profile-placeholder {
                    border: 2px dashed #adb5bd;
                }
                
                .btn-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .form-group {
                    margin-bottom: 1rem;
                }
                
                .form-control, .form-select {
                    padding: 0.5rem 0.75rem;
                    font-size: 0.9rem;
                }
                
                .form-label {
                    font-size: 0.9rem;
                    margin-bottom: 0.25rem;
                }
                
                .benefit-card {
                    transition: all 0.3s ease;
                }
                
                .benefit-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
                }
                
                .toast-success h6 {
                    font-size: 0.95rem;
                    font-weight: 600;
                }
                
                .toast-success p {
                    font-size: 0.85rem;
                    margin-bottom: 0.25rem;
                }
                
                @media (max-width: 576px) {
                    .container {
                        padding-left: 1rem;
                        padding-right: 1rem;
                    }
                    
                    .application-form {
                        padding: 1.5rem !important;
                    }
                    
                    .step-icon {
                        width: 32px;
                        height: 32px;
                        font-size: 0.9rem;
                    }
                    
                    .step-line {
                        top: 16px;
                    }
                    
                    h1.h2 {
                        font-size: 1.5rem;
                    }
                    
                    .profile-upload-container {
                        padding: 15px;
                    }
                    
                    .profile-preview,
                    .profile-placeholder {
                        width: 80px !important;
                        height: 80px !important;
                    }
                }
            `}</style>
            <Footer />
        </>
    );
};

export default ApplyAsATutor;